import { createQirtaasClient } from '@qirtaas/core'

const DAWWIN_API = 'https://api.dawwin.al-ilm.net'

export async function getQirtaasToken(): Promise<string> {
  const response = await fetch(`${DAWWIN_API}/qirtaas/token`, {
    method: 'POST',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  })
  if (!response.ok) throw new Error(`Qirtaas token request failed (${response.status})`)
  const data = (await response.json()) as { token?: string }
  if (!data.token) throw new Error('Qirtaas token response did not contain a token')
  return data.token
}

/**
 * A failed Dawwin document request, carrying the API's classification.
 * writeOutcome: 'not_applied' = the change was definitely not made;
 * 'unknown' = Qirtaas did not confirm in time, so the change may still have been applied.
 */
export class DawwinDocumentError extends Error {
  constructor(readonly status:number,readonly reason?:string,readonly writeOutcome?:'not_applied'|'unknown'){
    super(`Dawwin document request failed (${status}${reason?`, ${reason}`:''})`)
  }
}

async function dawwinDocumentFetch(path: string, init: RequestInit = {}) {
  const response = await fetch(`${DAWWIN_API}/qirtaas/documents${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(init.headers || {}),
    },
  })
  if (!response.ok) {
    const body = await response.json().catch(() => null) as { reason?: string; write_outcome?: 'not_applied'|'unknown' } | null
    // nginx's own 504 (no JSON body) on a write also means the outcome is unknown.
    const writeOutcome = body?.write_outcome ?? (init.method && init.method !== 'GET' && response.status === 504 ? 'unknown' : undefined)
    throw new DawwinDocumentError(response.status, body?.reason, writeOutcome)
  }
  return response
}

export type QirtaasDocumentSummary={id:string;title:string;created_at:string;updated_at:string}

// One shared document list for the whole app:
// - concurrent callers share a single in-flight request (unless the set of documents changed
//   after it started -- see listGeneration);
// - callers may accept a recent result (maxAgeMs) instead of refetching;
// - a failed request never replaces the last good list (callers keep what they had).
let listGeneration=0
let listCache:{data:QirtaasDocumentSummary[];startedAt:number;generation:number}|null=null
let listInflight:{promise:Promise<{documents:QirtaasDocumentSummary[];startedAt:number}>;generation:number}|null=null
/** Call after any create/delete/duplicate so later list requests cannot return a pre-change list. */
export function markDocumentListChanged(){listGeneration++}
export function documentListGeneration(){return listGeneration}
export function documentListAge(){return listCache?Date.now()-listCache.startedAt:Infinity}
export async function listDocumentsWithMeta(opts:{maxAgeMs?:number}={}):Promise<{documents:QirtaasDocumentSummary[];startedAt:number}>{
  const maxAge=opts.maxAgeMs??0
  if(listCache&&listCache.generation===listGeneration&&maxAge>0&&Date.now()-listCache.startedAt<=maxAge)return{documents:listCache.data,startedAt:listCache.startedAt}
  if(listInflight&&listInflight.generation===listGeneration)return listInflight.promise
  const generation=listGeneration,startedAt=Date.now()
  const promise=(async()=>{
    const response=await dawwinDocumentFetch('')
    const data=await response.json() as QirtaasDocumentSummary[]|{results?:QirtaasDocumentSummary[]}
    const documents=Array.isArray(data)?data:(data.results||[])
    if(!listCache||startedAt>=listCache.startedAt)listCache={data:documents,startedAt,generation}
    return{documents,startedAt}
  })()
  listInflight={promise,generation}
  promise.then(()=>{if(listInflight?.promise===promise)listInflight=null},()=>{if(listInflight?.promise===promise)listInflight=null})
  return promise
}
export async function listDocuments(opts:{maxAgeMs?:number}={}):Promise<QirtaasDocumentSummary[]>{
  return(await listDocumentsWithMeta(opts)).documents
}
function patchCachedSummary(id:string,changes:Partial<QirtaasDocumentSummary>){
  if(listCache)listCache={...listCache,data:listCache.data.map(d=>d.id===id?{...d,...changes}:d)}
}
export async function createDocument(content:Record<string,unknown>){try{return(await(await dawwinDocumentFetch('',{method:'POST',body:JSON.stringify({content})})).json())as{id:string;title:string;content:Record<string,unknown>|null}}finally{markDocumentListChanged()}}
export async function getDocumentContent(id:string,opts:{signal?:AbortSignal}={}){return(await(await dawwinDocumentFetch(`/${encodeURIComponent(id)}`,{signal:opts.signal})).json())as{id:string;title:string;content:Record<string,unknown>|null}}
export async function updateDocumentContent(id:string,content:Record<string,unknown>){return(await(await dawwinDocumentFetch(`/${encodeURIComponent(id)}`,{method:'PATCH',body:JSON.stringify({content})})).json())as{id:string;title:string;content:Record<string,unknown>|null}}
export async function renameDocument(id:string,title:string){const result=(await(await dawwinDocumentFetch(`/${encodeURIComponent(id)}`,{method:'PATCH',body:JSON.stringify({title})})).json())as{id:string;title:string;content:Record<string,unknown>|null};patchCachedSummary(id,{title:result?.title||title});return result}

export type ShareInfo={is_shared:boolean;share_token:string|null}
export async function getDocumentShareInfo(id:string){return(await(await dawwinDocumentFetch(`/${encodeURIComponent(id)}/share`)).json())as ShareInfo}
export async function setDocumentSharing(id:string,is_shared:boolean){return(await(await dawwinDocumentFetch(`/${encodeURIComponent(id)}/share`,{method:'PATCH',body:JSON.stringify({is_shared})})).json())as ShareInfo}

export async function downloadDocumentPdf(id:string,title:string){
  const response=await fetch(`${DAWWIN_API}/qirtaas/documents/${encodeURIComponent(id)}/pdf`,{credentials:'include',headers:{Accept:'application/pdf'}});
  if(!response.ok)throw new Error(`PDF export failed (${response.status})`);
  const blob=await response.blob(),url=URL.createObjectURL(blob),a=document.createElement('a');
  const safe=(title.trim().replace(/[\\/:*?"<>|]+/g,'-').replace(/\s+/g,' ').slice(0,100)||'Dawwin document')+'.pdf';
  a.href=url;a.download=safe;document.body.appendChild(a);a.click();a.remove();setTimeout(()=>URL.revokeObjectURL(url),1000);
}

const qirtaasClient=createQirtaasClient({getToken:getQirtaasToken})
// Same client, but deletes/duplicates mark the shared document list as changed.
export const qirtaas:typeof qirtaasClient=new Proxy(qirtaasClient,{get(target,prop,receiver){
  if(prop==='deleteDocument'||prop==='duplicateDocument'){
    const original=Reflect.get(target,prop,receiver) as (id:string)=>Promise<unknown>
    return async(id:string)=>{try{return await original.call(target,id)}finally{markDocumentListChanged()}}
  }
  const value=Reflect.get(target,prop,receiver)
  return typeof value==='function'?value.bind(target):value
}})

export async function unlinkCalendarDocument(id:string){const response=await fetch(`${DAWWIN_API}/calendar/event-notes/${encodeURIComponent(id)}`,{method:'DELETE',credentials:'include'});if(!response.ok)throw new Error(`Calendar note unlink failed (${response.status})`)}
